import { useState } from "react";
import type { Booking, CheckinVideo, Vehicle } from "@/types";
import { useCheckins } from "@/hooks/useCheckins";
import { CHECKIN_PHASE_ICONS, checkinClock, formatBytes, phaseLabel } from "@/lib/walkaround";
import { vehicleTitle } from "@/lib/format";
import CheckinRecorder from "./CheckinRecorder";
import CheckinPlayer from "./CheckinPlayer";
import AiFaultReport from "./AiFaultReport";
import DamageVerdict from "./DamageVerdict";

interface CarCheckinProps {
  booking: Booking;
  vehicle: Vehicle | null;
  viewer: "renter" | "host";
}

function timeAgo(value?: string): string {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const mins = Math.round((Date.now() - date.getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} hr ago`;
  const days = Math.round(hours / 24);
  return `${days} day${days > 1 ? "s" : ""} ago`;
}

export default function CarCheckin({ booking, vehicle, viewer }: CarCheckinProps) {
  const { checkins, loading, error, saving, inspectingId, startRecording, finishRecording, cancelRecording, inspect, reload } =
    useCheckins(booking.id, vehicle?.id ?? null);
  const [recordingPhase, setRecordingPhase] = useState<"pickup" | "return" | null>(null);
  const [playing, setPlaying] = useState<CheckinVideo | null>(null);
  const [aiError, setAiError] = useState("");

  const defaultPhase: "pickup" | "return" = booking.status === "completed" ? "return" : "pickup";
  const pickupCheckin = checkins.find((item) => item.phase === "pickup") ?? null;
  const returnCheckin = checkins.find((item) => item.phase === "return") ?? null;
  const canCompare = !!pickupCheckin && !!returnCheckin;
  const comparisonAi = returnCheckin?.ai_report ?? pickupCheckin?.ai_report ?? null;
  const title = vehicle ? vehicleTitle(vehicle.make, vehicle.model, vehicle.year) : "the car";

  async function handleInspect(checkin: CheckinVideo) {
    setAiError("");
    try {
      await inspect(checkin, { vehicle: title, viewer });
    } catch (err) {
      setAiError(err instanceof Error ? err.message : "The AI inspection couldn't run.");
    }
  }

  return (
    <section className="overflow-hidden rounded-2xl surface-card">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-background-200/70 px-5 py-4">
        <div className="min-w-0">
          <p className="flex items-center gap-2 font-heading text-sm font-semibold text-foreground-950">
            <i className="ri-video-download-line text-primary-500"></i> Car check-in video
          </p>
          <p className="mt-1 max-w-xl text-xs leading-relaxed text-foreground-600">
            {viewer === "host"
              ? "Film a real walkaround of the car before handover — the clip is stored on the booking so you and the rider can compare it after the ride."
              : "The host's walkaround clip is kept on the booking, so you can compare it when you hand the car back."}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setRecordingPhase(defaultPhase)}
          className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
        >
          <i className="ri-record-circle-line"></i> Record walkaround
        </button>
      </div>

      <div className="space-y-4 p-5">
        {loading && (
          <div className="space-y-3">
            <div className="h-20 w-full animate-pulse rounded-xl bg-background-200/40" />
            <div className="h-20 w-full animate-pulse rounded-xl bg-background-200/40" />
          </div>
        )}

        {!loading && error && (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-secondary-700/40 bg-secondary-950/40 px-4 py-3">
            <p className="flex items-center gap-2 text-sm text-secondary-300">
              <i className="ri-error-warning-line"></i> {error}
            </p>
            <button
              type="button"
              onClick={reload}
              className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-200 bg-background-100 px-3.5 py-2 text-xs font-medium text-foreground-900"
            >
              <i className="ri-refresh-line"></i> Try again
            </button>
          </div>
        )}

        {!loading && !error && checkins.length === 0 && (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200/60 text-2xl text-foreground-500">
              <i className="ri-video-line"></i>
            </span>
            <p className="mt-4 font-heading text-sm font-semibold text-foreground-950">No walkaround on record yet</p>
            <p className="mt-1 max-w-sm text-xs leading-relaxed text-foreground-600">
              A quick clip of the car before pickup protects both sides — record one and it'll be saved to this booking.
            </p>
          </div>
        )}

        {!loading &&
          !error &&
          checkins.map((checkin) => (
            <div key={checkin.id} className="rounded-xl border border-background-200/70 bg-background-100 p-3.5">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <button
                  type="button"
                  onClick={() => setPlaying(checkin)}
                  className="relative h-24 w-full shrink-0 overflow-hidden rounded-lg bg-background-200/50 sm:h-16 sm:w-24"
                  title={`Watch ${phaseLabel(checkin.phase)} walkaround`}
                >
                  {checkin.frames?.[0]?.url ? (
                    <img
                      src={checkin.frames[0].url}
                      alt={`${phaseLabel(checkin.phase)} walkaround`}
                      title={`${phaseLabel(checkin.phase)} — car check-in`}
                      className="h-full w-full object-cover object-top"
                    />
                  ) : (
                    <span className="flex h-full w-full items-center justify-center text-foreground-500">
                      <i className="ri-video-line text-xl"></i>
                    </span>
                  )}
                  <span className="absolute inset-0 flex items-center justify-center bg-background-50/30 text-xl text-background-50">
                    <i className="ri-play-circle-fill"></i>
                  </span>
                </button>

                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span
                      className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-1 text-[11px] font-medium ${
                        checkin.phase === "return" ? "bg-accent-100 text-accent-900" : "bg-primary-100 text-primary-900"
                      }`}
                    >
                      <i className={CHECKIN_PHASE_ICONS[checkin.phase] ?? "ri-video-line"}></i> {phaseLabel(checkin.phase)}
                    </span>
                    {checkin.video_path && (
                      <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-background-200/60 px-2.5 py-1 text-[11px] text-foreground-600">
                        <i className="ri-movie-2-line"></i> Recorded clip
                      </span>
                    )}
                    <span className="text-xs text-foreground-600">
                      {checkin.recorder_name || "Gari user"} · {timeAgo(checkin.created_at)}
                    </span>
                  </div>
                  <p className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-foreground-500">
                    <span className="inline-flex items-center gap-1">
                      <i className="ri-time-line"></i> {checkinClock(checkin.duration_seconds ?? 0)}
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <i className="ri-image-2-line"></i> {checkin.frames?.length ?? 0} stills
                    </span>
                    {checkin.video_size ? (
                      <span className="inline-flex items-center gap-1">
                        <i className="ri-hard-drive-2-line"></i> {formatBytes(checkin.video_size)}
                      </span>
                    ) : null}
                  </p>
                  {checkin.note && (
                    <p className="mt-1.5 line-clamp-2 text-xs leading-relaxed text-foreground-700">{checkin.note}</p>
                  )}
                </div>

                <div className="flex flex-col gap-2 sm:items-stretch">
                  <button
                    type="button"
                    onClick={() => setPlaying(checkin)}
                    className="inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-background-200 bg-background-50 px-4 py-2.5 text-sm font-medium text-foreground-900 transition-colors hover:border-primary-500/60"
                  >
                    <i className="ri-play-line"></i> Watch
                  </button>
                  <button
                    type="button"
                    onClick={() => handleInspect(checkin)}
                    disabled={inspectingId === checkin.id}
                    className="inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-background-200 bg-background-50 px-4 py-2.5 text-sm font-medium text-foreground-900 transition-colors hover:border-primary-500/60 disabled:opacity-50"
                  >
                    {inspectingId === checkin.id ? (
                      <i className="ri-loader-4-line animate-spin"></i>
                    ) : (
                      <i className="ri-sparkling-2-line"></i>
                    )}
                    {checkin.ai_report ? "Re-inspect" : "AI inspect"}
                  </button>
                </div>
              </div>

              {checkin.ai_report && (
                <div className="mt-3">
                  <AiFaultReport report={checkin.ai_report} compact />
                </div>
              )}
            </div>
          ))}

        {aiError && (
          <p className="flex items-start gap-2 rounded-lg border border-secondary-700/40 bg-secondary-950/40 px-3.5 py-2.5 text-sm text-secondary-300">
            <i className="ri-error-warning-line mt-0.5"></i> {aiError}
          </p>
        )}

        {canCompare && pickupCheckin && returnCheckin && (
          <div className="rounded-xl border border-accent-500/30 bg-accent-950/30 p-4">
            <p className="flex items-center gap-2 text-sm font-medium text-foreground-950">
              <i className="ri-scales-3-line text-accent-400"></i> Before pickup vs after ride
            </p>
            <p className="mt-1 text-xs leading-relaxed text-foreground-600">
              Both walkarounds are stored on this booking. Line them up to spot any change in condition.
            </p>
            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
              {[pickupCheckin, returnCheckin].map((item) => (
                <div key={item.id} className="overflow-hidden rounded-lg border border-background-200/70 bg-background-100">
                  <div className="relative aspect-video w-full overflow-hidden bg-background-200/50">
                    {item.frames?.[0]?.url ? (
                      <img
                        src={item.frames[0].url}
                        alt={`${phaseLabel(item.phase)} walkaround`}
                        title={`${phaseLabel(item.phase)} — car check-in`}
                        className="h-full w-full object-cover object-top"
                      />
                    ) : (
                      <span className="flex h-full w-full items-center justify-center text-foreground-500">
                        <i className="ri-video-line text-xl"></i>
                      </span>
                    )}
                    <span className="absolute left-2 top-2 rounded-full bg-background-50/85 px-2.5 py-0.5 text-[11px] font-medium text-foreground-900 backdrop-blur">
                      {phaseLabel(item.phase)}
                    </span>
                  </div>
                  <div className="space-y-1.5 p-3">
                    <p className="flex items-center justify-between text-xs text-foreground-600">
                      <span>{timeAgo(item.created_at)}</span>
                      <span className="inline-flex items-center gap-1">
                        <i className="ri-time-line"></i> {checkinClock(item.duration_seconds ?? 0)}
                      </span>
                    </p>
                    <p className="line-clamp-2 text-xs leading-relaxed text-foreground-700">
                      {item.note || "No condition notes were added."}
                    </p>
                    <button
                      type="button"
                      onClick={() => setPlaying(item)}
                      className="mt-1 inline-flex items-center gap-1.5 whitespace-nowrap text-xs font-medium text-primary-600 transition-colors hover:text-primary-500"
                    >
                      <i className="ri-play-circle-line"></i> Watch this clip
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <DamageVerdict bookingId={booking.id} aiReport={comparisonAi} />
          </div>
        )}
      </div>

      {recordingPhase && (
        <CheckinRecorder
          bookingId={booking.id}
          defaultPhase={recordingPhase}
          saving={saving}
          onStart={(phase) => startRecording({ phase, recorderRole: viewer })}
          onFinish={async (payload) => {
            await finishRecording(payload.recordingId, {
              phase: payload.phase,
              recorderRole: viewer,
              frames: payload.frames,
              durationSeconds: payload.durationSeconds,
              note: payload.note,
              videoPath: payload.videoPath,
              videoMime: payload.videoMime,
              videoSize: payload.videoSize,
            });
            setRecordingPhase(null);
          }}
          onCancel={(id) => {
            cancelRecording(id);
            setRecordingPhase(null);
          }}
        />
      )}

      {playing && <CheckinPlayer checkin={playing} onClose={() => setPlaying(null)} />}
    </section>
  );
}